import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Organization } from "../../../../core/models/entities/Organization";
import { Manufacturer } from "../../../../core/models/entities/Manufacturer";
import { LogoOptions } from "../../../../core/models/entities/LogoOptions";
import { User } from "../../../../core/models/entities/User";
import {
  Commission,
  CommissionProfile,
} from "../../../../core/models/entities/Commission";
import { CreateUserDto } from "../../../../core/models/dtos/CreateUserDto";
import { UpdateUserDto } from "../../../../core/models/dtos/UpdateUserDto";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { useWorkshopMode } from "../../../../hooks/useWorkshopMode";
import { useErrorMessage } from "../../../hooks/useErrorMessage";
import { withToasts } from "../../../components/Toast/Toast";
import { useCustomerActionHandler } from "../../customer-create/hooks/useCustomerActionHandler";
import { CommissionOptions } from "../component/commission-editor.component";
import {
  OrganizationUpdateForm,
  OrgSectionId,
  hasDifferingShippingAddress,
  isCustomerUpdateFormInvalid,
  resolveShippingFields,
  toOrganizationUpdateForm,
} from "../models/organization-update.form";
import {
  FieldErrorMap,
  RequiredFieldError,
  UPDATE_COMPANY_FIELD_ORDER,
  buildMissingFieldsMessage,
  getCompanyFieldErrors,
  getShippingFieldErrors,
  scrollToFirstErrorField,
  toFieldErrorMap,
} from "../models/required-fields";

export interface CustomerUpdateForm_VM {
  organization?: Organization;
  isVendor?: boolean;
  form: OrganizationUpdateForm;
  setForm: (form: OrganizationUpdateForm) => void;
  /** Required-field errors by field name (customers only; empty for vendors). */
  fieldErrors: FieldErrorMap;
  /** True after a save attempt was blocked — sections show every error. */
  showFieldErrors: boolean;
  /** "Please fill in: ..." line for the footer after a blocked save. */
  validationSummary?: string;
  isDirty: boolean;
  isLoading: boolean;
  activeSection: OrgSectionId;
  onSectionChange: (section: OrgSectionId) => void;
  /**
   * Warms a section's query caches ahead of the click (nav hover).
   * Fail-silent and idempotent; currently only Pricing has warmable data.
   */
  onSectionPrefetch?: (section: OrgSectionId) => void;
  onSave: () => void;
  onCancel: () => void;
  shippingDiffers: boolean;
  onShippingDiffersChange: (differs: boolean) => void;
  pricing: {
    commission: CommissionProfile;
    commissionOptions: CommissionOptions;
    updateCommission: (commission: CommissionProfile) => void;
    logoOptions?: LogoOptions;
    optionsReady: boolean;
  };
  brands: {
    manufacturers?: Manufacturer[];
    isLoading: boolean;
  };
  users: {
    users?: User[];
    isLoading: boolean;
    onCreateUser: (user: CreateUserDto) => Promise<void>;
    onDeleteUser: (userId: number) => Promise<void>;
    onUpdateUser: (user: User, dto: UpdateUserDto) => Promise<void>;
    onToggleUserHidePrices: (user: User, hidePrices: boolean) => Promise<void>;
  };
}

export function useCustomerFormUpdateViewModel(
  customer?: Organization,
  isVendor?: boolean
): CustomerUpdateForm_VM {
  const navigate = useNavigate();
  const {
    logoRepository,
    organizationRepository,
    userRepository,
    manufacturerRepository,
  } = useRepositoriesContext();
  const { profile } = useProfileContext();
  const workshopMode = useWorkshopMode();
  const { tError } = useErrorMessage();
  const { handleGoToCustomerList, handleUpdate, isLoading } =
    useCustomerActionHandler();

  // ----- Section state (drives lazy data loading per section) -----
  const [activeSection, setActiveSection] = useState<OrgSectionId>("details");
  const [visitedSections, setVisitedSections] = useState<Set<OrgSectionId>>(
    () => new Set<OrgSectionId>(["details"])
  );

  function handleSectionChange(section: OrgSectionId) {
    setActiveSection(section);
    setVisitedSections((prev) => {
      if (prev.has(section)) return prev;
      const next = new Set(prev);
      next.add(section);
      return next;
    });
  }

  // ----- Data -----
  // The vendor is needed for the VAT fallback that is part of the save body,
  //  so it stays eager (single small request, usually cached).
  const { data: vendor } = organizationRepository.useVendor(
    profile?.userOrganizationInformations.organizationId
  );

  // Pricing data mounts on first open of the Pricing section.
  const pricingVisited = visitedSections.has("pricing");
  const { data: logoOptions, isSuccess: isLogoOptionsSuccess } =
    logoRepository.useOptions({ enabled: pricingVisited });
  const { data: transferOptions, isSuccess: isTransferOptionsSuccess } =
    logoRepository.useTransferOptions(customer?.parentId, {
      enabled: pricingVisited,
    });

  // Brands data (manufacturers master list) mounts on first open of Brands.
  const brandsVisited = visitedSections.has("brands");
  const { data: manufacturers, isLoading: isManufacturersLoading } =
    manufacturerRepository.useManufacturers({ enabled: brandsVisited });

  // Users mount on first open of Users & contact.
  const usersVisited = visitedSections.has("users");
  const { data: users, isLoading: isUsersLoading } =
    userRepository.useUsersOfOrganization(customer?.id ?? 0, {
      enabled: !!customer?.id && usersVisited,
    });

  // ----- Pricing prefetch -----
  // The Pricing data stays lazily gated above (mounts on first visit), but we
  // warm its exact query caches in the background AFTER first paint so "Open
  // editor" is usually instant: GetTemplatesSizes is a heavy NAV call (p50
  // ~0.8s, p95 ~6s) that must never gate the screen's first paint.
  // Fail-silent — on a miss the lazy hooks fetch exactly as before (the
  // spinner becomes the fallback, not the norm). No prefetch for
  // workshop-flagged users (Pricing is hidden for them) or when editing a
  // vendor org (no Pricing section).
  const pricingPrefetchFired = useRef(false);
  function prefetchPricingData() {
    if (isVendor || workshopMode || !customer) return;
    if (pricingPrefetchFired.current) return;
    pricingPrefetchFired.current = true;
    logoRepository.prefetchOptions();
    // Same vendorId the lazy hook above passes, so the cache key matches.
    logoRepository.prefetchTransferOptions(customer.parentId);
  }

  useEffect(() => {
    if (isVendor || workshopMode || !customer) return;
    // Idle-ish delay: well past the screen's first paint, long before a
    // typical section click. Hovering the Pricing nav item prefetches earlier.
    const timer = setTimeout(prefetchPricingData, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVendor, workshopMode, customer?.id]);

  function handleSectionPrefetch(section: OrgSectionId) {
    if (section === "pricing") prefetchPricingData();
  }

  // ----- Form state -----
  const [form, setForm] = useState<OrganizationUpdateForm>(
    toOrganizationUpdateForm(customer, vendor?.defaultVatRate)
  );
  const [shippingDiffers, setShippingDiffers] = useState<boolean>(
    hasDifferingShippingAddress(customer)
  );
  const [commission, setCommission] = useState<CommissionProfile | undefined>(
    customer?.commission
  );
  const [commissionDirty, setCommissionDirty] = useState(false);
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [userActionLoading, setUserActionLoading] = useState(false);

  const baselineRef = useRef<string>(
    JSON.stringify(toOrganizationUpdateForm(customer, vendor?.defaultVatRate))
  );
  const shippingDiffersBaselineRef = useRef<boolean>(
    hasDifferingShippingAddress(customer)
  );

  useEffect(() => {
    // Reinitialize the form whenever the customer changes (navigation or
    //  refetch after save). Also resets the dirty baseline.
    const nextForm = toOrganizationUpdateForm(customer, vendor?.defaultVatRate);
    const nextDiffers = hasDifferingShippingAddress(customer);
    setForm(nextForm);
    setShippingDiffers(nextDiffers);
    setCommission(customer?.commission);
    setCommissionDirty(false);
    baselineRef.current = JSON.stringify(nextForm);
    shippingDiffersBaselineRef.current = nextDiffers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, vendor]);

  const isDirty =
    JSON.stringify(form) !== baselineRef.current ||
    shippingDiffers !== shippingDiffersBaselineRef.current ||
    commissionDirty;

  // ----- Required-field validation (customers only — vendor company fields
  // ----- are ERP-locked and stay untouched) -----
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const requiredFieldErrors: RequiredFieldError[] = isVendor
    ? []
    : [
        ...getCompanyFieldErrors(form, UPDATE_COMPANY_FIELD_ORDER),
        ...getShippingFieldErrors(form, shippingDiffers),
      ];

  // ----- Save -----
  function handleSaveClick() {
    if (requiredFieldErrors.length > 0) {
      setShowFieldErrors(true);
      // Jump to the section holding the first missing field and scroll to it.
      const firstField = requiredFieldErrors[0].field;
      handleSectionChange(
        firstField.indexOf("shipping") === 0 ? "shipping" : "details"
      );
      setTimeout(() => scrollToFirstErrorField(requiredFieldErrors), 100);
      return;
    }

    const formValid = !isCustomerUpdateFormInvalid(form);
    if (!formValid) {
      toast.error(
        "The form is incomplete — company and contact details are required"
      );
      return;
    }

    const resolved = resolveShippingFields(
      form,
      shippingDiffers,
      shippingDiffersBaselineRef.current
    );
    const { excludedManufacturerIds, ...body } = resolved;

    async function transaction() {
      await handleUpdate({
        ...body,
        isVendor: false,
        excludedManufacturers: excludedManufacturerIds,
        commission: undefined,
      } as unknown as Organization);
      if (commission && customer) {
        setCommissionLoading(true);
        try {
          await organizationRepository.setCommissionProfile(
            customer.id,
            commission
          );
        } finally {
          setCommissionLoading(false);
        }
      }
    }

    transaction()
      .then(() =>
        toast.success(isVendor ? "Vendor was updated" : "Customer was updated")
      )
      .catch((e) => tError(e));
  }

  function handleCancel() {
    if (customer?.isVendor) {
      navigate("/vendor");
    } else {
      handleGoToCustomerList();
    }
  }

  // ----- Commission -----
  function updateCommission(next: CommissionProfile): void {
    setCommission(next);
    setCommissionDirty(true);
  }

  // ----- Users -----
  function handleCreateUser(user: CreateUserDto) {
    async function transaction() {
      user.organizationId = customer?.id ?? 0;
      return await userRepository.createUser(user);
    }

    return withToasts(
      transaction,
      "Creating user",
      "User created",
      "",
      setUserActionLoading
    );
  }

  function handleDeleteUser(userId: number) {
    async function transaction() {
      return await userRepository.deleteUser(userId);
    }

    return withToasts(
      transaction,
      "Deleting user",
      "User deleted",
      "Failed to delete user",
      setUserActionLoading
    );
  }

  function handleUpdateUser(user: User, dto: UpdateUserDto) {
    async function transaction() {
      return await userRepository.updateUser(user, customer?.id ?? 0, dto);
    }

    return withToasts(
      transaction,
      "Updating user",
      "User updated",
      "Failed to update user",
      setUserActionLoading
    );
  }

  function handleToggleUserHidePrices(user: User, hidePrices: boolean) {
    async function transaction() {
      return await userRepository.setUserHidePrices(
        customer?.id ?? 0,
        user.id,
        hidePrices
      );
    }

    return withToasts(
      transaction,
      "Updating price visibility",
      hidePrices
        ? "Prices are now hidden for this user"
        : "Prices are now visible for this user",
      "Failed to update price visibility",
      setUserActionLoading
    );
  }

  return {
    organization: customer,
    isVendor,
    form,
    setForm,
    fieldErrors: toFieldErrorMap(requiredFieldErrors),
    showFieldErrors,
    validationSummary: showFieldErrors
      ? buildMissingFieldsMessage(requiredFieldErrors)
      : undefined,
    isDirty,
    isLoading: isLoading || commissionLoading || userActionLoading,
    activeSection,
    onSectionChange: handleSectionChange,
    onSectionPrefetch: handleSectionPrefetch,
    onSave: handleSaveClick,
    onCancel: handleCancel,
    shippingDiffers,
    onShippingDiffersChange: setShippingDiffers,
    pricing: {
      commission:
        commission ??
        ({
          typeOnly: [] as Commission[],
          methods: [] as Commission[],
        } as CommissionProfile),
      commissionOptions: {
        types: logoOptions?.transferLogoTypes ?? [],
        transfers: transferOptions?.transferMethods ?? [],
      },
      updateCommission,
      logoOptions,
      optionsReady: isLogoOptionsSuccess && isTransferOptionsSuccess,
    },
    brands: {
      manufacturers,
      isLoading: brandsVisited && isManufacturersLoading,
    },
    users: {
      users,
      isLoading: usersVisited && isUsersLoading,
      onCreateUser: handleCreateUser,
      onDeleteUser: handleDeleteUser,
      onUpdateUser: handleUpdateUser,
      onToggleUserHidePrices: handleToggleUserHidePrices,
    },
  };
}
